import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAPR } from '../context/APRContext';
import { useAuth } from '../context/AuthContext';
import { Application, Question, ApplicationResponse } from '../types';

const ApplicationQuestionnaire: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const [searchParams] = useSearchParams();
  const { state, dispatch } = useAPR();
  const { currentUser } = useAuth();
  
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Get the application
  const application = state.applications.find(app => app.id === applicationId);

  // Get all questions from the framework
  const getAllQuestions = (): Question[] => {
    const questions: Question[] = [];
    
    if (state.framework?.dimensions) {
      state.framework.dimensions.forEach(dimension => {
        if (dimension.components) {
          dimension.components.forEach(component => {
            if (component.attributes) {
              component.attributes.forEach(attribute => {
                if (attribute.question) {
                  questions.push(attribute.question);
                }
              });
            }
          });
        }
      });
    }
    
    return questions;
  };

  const allQuestions = getAllQuestions();

  // Load existing responses for this application
  useEffect(() => {
    if (application) {
      const existingResponses: Record<string, string> = {};
      application.responses?.forEach(response => {
        existingResponses[response.questionId] = response.selectedAnswerId;
      });
      setResponses(existingResponses);
    }
  }, [application]);

  // Calculate completion status
  useEffect(() => {
    const answeredCount = Object.keys(responses).length;
    const totalQuestions = allQuestions.length;
    
    if (answeredCount === totalQuestions && totalQuestions > 0) {
      setIsComplete(true);
    }
  }, [responses, allQuestions.length]);

  if (!application) {
    return (
      <div className="questionnaire-container">
        <div className="questionnaire-error">
          <h2>Application Not Found</h2>
          <p>The requested application could not be found.</p>
        </div>
      </div>
    );
  }

  // Check if this is a parent application - parent apps cannot be scored
  if (application.isParent) {
    return (
      <div className="questionnaire-container">
        <div className="questionnaire-error" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          textAlign: 'center',
          minHeight: '400px'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚫</div>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#dc2626' }}>Parent Application</h2>
          <p style={{ marginBottom: '0.5rem', color: '#666', maxWidth: '500px' }}>
            <strong>{application.name}</strong> is a parent application and cannot be scored.
          </p>
          <p style={{ marginBottom: '2rem', color: '#666', maxWidth: '500px' }}>
            Only child applications are eligible for Q&A assessment and scoring.
            Please select one of this application's child applications instead.
          </p>
          <a href="/apr/scoring" style={{
            padding: '12px 24px',
            backgroundColor: '#007bff',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}>
            Return to Application Scoring
          </a>
        </div>
      </div>
    );
  }

  const handleAnswerSelect = (questionId: string, answerId: string, score: number) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: answerId
    }));

    // Update application responses in the context
    const newResponse: ApplicationResponse = {
      id: `response_${Date.now()}`,
      applicationId: application.id,
      questionId,
      selectedAnswerId: answerId,
      score
    };

    // Update responses array
    const updatedResponses = application.responses ? [...application.responses] : [];
    const existingIndex = updatedResponses.findIndex(r => r.questionId === questionId);
    
    if (existingIndex >= 0) {
      updatedResponses[existingIndex] = newResponse;
    } else {
      updatedResponses.push(newResponse);
    }

    // Update application in context
    const updatedApplication: Application = {
      ...application,
      responses: updatedResponses
    };

    dispatch({
      type: 'UPDATE_APPLICATION',
      payload: updatedApplication
    });
  };

  const getQuestionStatus = (questionId: string) => {
    return responses[questionId] ? 'answered' : 'pending';
  };

  const getAnsweredCount = () => {
    return Object.keys(responses).length;
  };

  const handleQuestionClick = (index: number) => {
    setCurrentQuestionIndex(index);
  };

  const currentQuestion = allQuestions[currentQuestionIndex];
  const progress = allQuestions.length > 0 ? ((getAnsweredCount() / allQuestions.length) * 100).toFixed(1) : '0';

  // Handle case where no questions are available
  if (allQuestions.length === 0) {
    return (
      <div className="questionnaire-container">
        <div className="questionnaire-header">
          <div className="questionnaire-header__info">
            <h1 className="questionnaire-title">Application Portfolio Q&A</h1>
            <div className="application-info">
              <h2 className="application-name">{application?.name || 'Application'}</h2>
              {application?.applicationIdCode && (
                <span className="application-id">ID: {application.applicationIdCode}</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="empty-questions-state" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          textAlign: 'center',
          minHeight: '400px'
        }}>
          <div className="empty-state-icon" style={{ fontSize: '4rem', marginBottom: '1rem' }}>📝</div>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#333' }}>No Questions Available</h3>
          <p style={{ marginBottom: '0.5rem', color: '#666', maxWidth: '500px' }}>
            There are currently no assessment questions configured for this framework.
          </p>
          <p style={{ marginBottom: '2rem', color: '#666', maxWidth: '500px' }}>
            Questions are automatically created when attributes are defined with assessment criteria.
          </p>
          <a href="/framework/attributes" style={{
            padding: '12px 24px',
            backgroundColor: '#007bff',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }} onMouseOver={(e) => (e.target as HTMLElement).style.backgroundColor = '#0056b3'}
             onMouseOut={(e) => (e.target as HTMLElement).style.backgroundColor = '#007bff'}>
            Configure Attributes & Questions
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="questionnaire-container">
      {/* Header */}
      <div className="questionnaire-header">
        <div className="questionnaire-header__info">
          <h1 className="questionnaire-title">Application Portfolio Q&A</h1>
          <div className="application-info">
            <h2 className="application-name">{application?.name || 'Application'}</h2>
            {application?.applicationIdCode && (
              <span className="application-id">ID: {application.applicationIdCode}</span>
            )}
          </div>
        </div>
        
        <div className="questionnaire-header__progress">
          <div className="progress-info">
            <span className="progress-text">
              {getAnsweredCount()} of {allQuestions.length} questions answered
            </span>
            <div className="progress-bar">
              <div 
                className="progress-bar__fill" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <span className="progress-percentage">{progress}%</span>
          </div>
          
          {isComplete && (
            <div className="completion-status">
              <span className="completion-badge">✅ Complete</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="questionnaire-content">
        {/* Questions Sidebar */}
        <div className="questions-sidebar">
          <h3 className="sidebar-title">All Questions ({allQuestions.length})</h3>
          <div className="questions-list">
            {allQuestions.map((question, index) => {
              const status = getQuestionStatus(question.id);
              return (
                <div 
                  key={question.id} 
                  className={`question-item ${status} ${index === currentQuestionIndex ? 'active' : ''}`}
                  onClick={() => handleQuestionClick(index)}
                >
                  <div className="question-item__number">{index + 1}</div>
                  <div className="question-item__content">
                    <div className="question-item__text">{question.text}</div>
                    <div className="question-item__status">
                      {status === 'answered' ? (
                        <span className="status-answered">✅ Answered</span>
                      ) : (
                        <span className="status-pending">⏳ Pending</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Current Question */}
        <div className="question-panel">
          {currentQuestion && (
            <div className="current-question">
              <div className="question-header">
                <div className="question-number">
                  Question {currentQuestionIndex + 1} of {allQuestions.length}
                </div>
              </div>
              
              <div className="question-content">
                <h3 className="question-text">{currentQuestion.text}</h3>
                
                <div className="answer-options">
                  {(currentQuestion.answerOptions || []).map(option => {
                    const isSelected = responses[currentQuestion.id] === option.id;
                    return (
                      <div 
                        key={option.id}
                        className={`answer-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleAnswerSelect(currentQuestion.id, option.id, option.score)}
                      >
                        <div className="answer-option__radio">
                          <input
                            type="radio"
                            name={`question_${currentQuestion.id}`}
                            value={option.id}
                            checked={isSelected}
                            onChange={() => handleAnswerSelect(currentQuestion.id, option.id, option.score)}
                          />
                        </div>
                        <div className="answer-option__content">
                          <div className="answer-option__text">{option.text}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Navigation */}
              <div className="question-navigation">
                <button 
                  className="nav-button nav-button--prev"
                  onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                  disabled={currentQuestionIndex === 0}
                >
                  ← Previous
                </button>
                
                <span className="question-counter">
                  {currentQuestionIndex + 1} / {allQuestions.length}
                </span>
                
                <button 
                  className="nav-button nav-button--next"
                  onClick={() => setCurrentQuestionIndex(Math.min(allQuestions.length - 1, currentQuestionIndex + 1))}
                  disabled={currentQuestionIndex === allQuestions.length - 1}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationQuestionnaire;